import { useCallback } from "react";
import { getDefaultStore, useAtomValue, WritableAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

const store = getDefaultStore();

const FLAG_PREFIX = "flag:";

type FlagAtom = WritableAtom<boolean | null, [boolean | null], void>;

const flagAtoms = new Map<string, FlagAtom>();

function featureFlagAtom(key: string): FlagAtom {
  let atom = flagAtoms.get(key);

  if (!atom) {
    atom = atomWithStorage<boolean | null>(
      `${FLAG_PREFIX}${key}`,
      null,
      createJSONStorage(() => localStorage),
      { getOnInit: true }
    );
    flagAtoms.set(key, atom);
  }

  return atom;
}

/**
 * Feature flags, backed by the browser localStorage.
 *
 * These feature flags can be used for client-specific toggles which can be
 * enabled or disabled as required, or left unset if a "hidden" feature is
 * desired. Flags will default to `false` if they have not been initialized
 * or set, and can only store boolean values.
 */
export const featureFlags = {
  /**
   * Initialize a feature flag with a default value if it doesn't yet exist.
   *
   * If the value does exist and has been previously set or initialized, the
   * existing value will be used.
   */
  init(key: string, value: boolean): boolean {
    const initial = store.get(featureFlagAtom(key));

    if (initial === null) {
      store.set(featureFlagAtom(key), value);
      return value;
    } else {
      return initial;
    }
  },

  /**
   * Retrieve a feature flag value.
   */
  get(key: string): boolean {
    return !!store.get(featureFlagAtom(key));
  },

  /**
   * Set a feature flag value.
   */
  set(key: string, value: boolean): void {
    store.set(featureFlagAtom(key), value);
  },

  /**
   * Remove a feature flag value.
   */
  remove(key: string): void {
    localStorage.removeItem(`${FLAG_PREFIX}${key}`);
  },

  /**
   * Retrieve all feature flag keys.
   */
  keys(): string[] {
    const keys = Object.keys(localStorage)
      .map(String)
      .filter((key) => key.startsWith(FLAG_PREFIX))
      .map((key) => key.slice(FLAG_PREFIX.length));

    return [...new Set(keys)];
  },

  /**
   * Retrieve all feature flag values as an object.
   */
  toObject(): Record<string, boolean> {
    const entries = featureFlags
      .keys()
      .map((key) => [key, featureFlags.get(key)] as const);
    return Object.fromEntries(entries);
  },
} as const;

/**
 * Hook for getting or setting a feature flag and optionally initializing it.
 *
 * This hook will return the current value of the feature flag and a setter,
 * similar to the `useState` hook. If the feature flag has not been initialized,
 * it will default to `false`, otherwise it will use the stored flag value, or
 * the specified `init` value if provided when there is not stored value.
 */
export const useFeatureFlag = (
  flag: string,
  init?: boolean
): [boolean, (v: boolean) => void] => {
  if (init !== undefined) {
    featureFlags.init(flag, init);
  }

  const value = useFeatureFlagValue(flag);
  const setValue = useCallback(
    (v: boolean) => featureFlags.set(flag, v),
    [flag]
  );
  return [value, setValue];
};

/**
 * Hook for using a feature flag value.
 *
 * This hook behaves like the `useFeatureFlag` hook, but only returns the flag
 * value, without the setter.
 */
export const useFeatureFlagValue = (flag: string): boolean => {
  return !!useAtomValue(featureFlagAtom(flag));
};

window.featureFlags = featureFlags;
